import * as d3 from 'd3-selection'
import tooltipContents from './contents'
import * as colors from 'colors'

const getTotal = (series, range) => {
  return series.reduce((p, c) => {
    let point = getPoint(c.points, range)
    if (point && point.raw) {
      p += point.raw
    }
    return p
  }, 0)
}

const getPoint = (points, range) => {
  let p = points.find(p => {
    if (!range) {
      // No range, probably a no-breakdown chart w/o a calculated breakdown
      return true
    }
    return p.rangeId == range.id
  })

  if (!p) {
    return undefined
  }

  return Object.assign(p, {
    formatted: null // Clear the formatted property. Tooltips always use the serie formatter
  })
}

const getMultiRowColor = (serie, point) => {
  if (serie.type === 'bar') {
    // Use the point color if defined
    return point.color || serie.color
  } else {
    // Use the serie color
    return serie.color
  }
}

let tooltipRows = function (data, scalers, scale, event, chart) {
  let eventX = event.pageX - (document.documentElement.scrollLeft ? document.documentElement.scrollLeft : document.body.scrollLeft)
    , pos = eventX - this.getBoundingClientRect().left - (chart.layout.rtl ? chart.layout.rightMarginOffset : 0) // Remove the space showing name labels if RTL
    , breakdown = chart.layout.breakdown
    , i = Math.floor(((1 / scale) * pos) / scalers.x.groupScale.step())
    , range = breakdown.range

  if (chart.layout.rtl) {
    // RTL, reverse the range direction
    range = range.slice().reverse()
  }

  let x = range[Math.max(0, Math.min(i, range.length - 1))] // Math.max/Math.min used to make sure we always grab a value within the range. See SB3 #20277
    , total = getTotal(data.series, x)
    , config = chart.datum().tooltip
    , series = data.series.filter(serie => serie.type !== 'reference-band') // Ignore ref bands for now. See Impact #42133
    , rows = series.map(s => {
      let point = getPoint(s.points, x)
      if (!point && s.forecast) {
        // See if we have forecast data on this serie
        point = getPoint(s.forecast.points, x)
      }

      if (s.type === 'regression') {
        // regressions
        let prediction = s.prediction(i + 1)
        point = {
          slope: s.prediction(i + 2) - s.prediction(i),
          formatted: s.shortenedAndUnshortenedFormatter(prediction).shortened,
          range: x,
          raw: prediction,
          _range: [0, prediction]
        }
      }

      if (!point || point.raw === undefined) {
        return null
      }

      let cols = []
        , axis = s.axis || 'left'
        , format = s.formatter || (d => d) // Use the serie formatter

      if (series.length === 1 || (breakdown.calculated && breakdown.range.length > 0 && !s.stackBy)) {
        // One column displayed. This is used when we're showing a single serie
        // or we have a calculated breakdown with a mock range (i.e. multiple bars)

        let color = point.performanceColor || point.color || ''

        if (color == 'gray') {
          // To make sure the tooltip $score-colors.gray value doesn't come across as
          // too light, use $score-slate instead.
          // https://github.com/SpiderStrategies/Scoreboard/issues/25086
          color = 'score-slate'
        }

        cols.push({
          text: point.formatted || format(point.raw),
          color: colors.lookup(color)
        })
      } else {
        // Multiple serie styling where we show multiple columns
        let color = getMultiRowColor(s, point)
        cols.push({clazz: `color-cell`, vars: {
          name: 'colored-cell-color',
          value: colors.lookup(color)
        }}) // Color block

        if (point.slope !== undefined) {
          // Change the class of the first cell to adjust for regression slopes
          let arrow = 'flat' // default
          if (point.slope < 0) {
            arrow = 'below'
          } else if (point.slope > 0) {
            arrow = 'above'
          }
          cols[0].clazz += ` regression-arrow regression-arrow-${arrow}`
        }

        cols.push({text: point.formatted || format(point.raw), clazz: 'raw-value'})

        if (s.offsetExpand && s.type === 'bar') {
          // stacked 100% bar chart
          // Add percentages next to the numbers
          cols.push({text: `(${config.percentage(point.raw / total)})`})
        }

        cols.push({text: s.title, clazz: 'last-cell'})
      }

      let header = x && [`${x.title || ''} ${x.subtitle || ''}`]
        , headerOverride = !!config.headerFormatter

      if (headerOverride) {
        // They are overriding the header. Will return an array of the rows
        header = config.headerFormatter(data, x)
      }

      return {
        raw: point.raw,
        range: point._range,
        slope: point.slope,
        rangeId: x && x.id,
        format,
        stackBy: s.stackBy,
        headerOverride,
        header,
        axis,
        cols
      }
    }).filter(Boolean).reverse() // so series are listed top down

  return rows
}

export default (tooltip, e, data, scalers) => {
  let contents = tooltip.contents || tooltipContents
    , rows = tooltip.rows || tooltipRows
    , chart = tooltip.chart
    , box = tooltip.box.getBoundingClientRect()

  let node = chart.node()
    , scale = node.ownerSVGElement.getBoundingClientRect().width / parseInt(d3.select(node.ownerSVGElement).style('width'))
    , _rows = rows.call(tooltip.box, data, scalers, scale, e, chart)
    , highest = _rows.length && _rows.reduce(function (p, c) {
        return scalers.y[p.axis](p.range[1]) < scalers.y[c.axis](c.range[1]) ? p : c
      })
    , html = contents(_rows, chart)
    , x = box.left + scalers.x.groupScale.range()[1] / 2 // By default, center the tooltip

  if (!highest || highest.raw === undefined) {
     // They are hovered on a location that doesn't have a value
    return
  }

  if (highest.rangeId !== undefined) {
    // Center the tooltip on the center of the range's tick if
    // the highest point is tied to a range
    let xRangePos = scalers.x.center(chart.xAccessor.call(this, highest)) * scale
    if (chart.layout.rtl) {
      // if rtl, we need to align the right side of the tooltip with the right side of the range
      x = box.right - xRangePos
    } else {
      x = box.left + xRangePos
    }
  }

  let yScale = scalers.y[highest.axis]
    , _y = yScale(highest.range[1])
    , bounds = yScale.range()
    , flip = false

  if (_y < bounds[1]) {
    // Value is above, clamp it and make sure the tooltip flips
    _y = bounds[1]
    flip = true
  }

  if (_y > bounds[0]) {
    _y = bounds[0]
  }

  let y = box.top + _y * scale

  tooltip._html(html)
         ._setFlip(flip)
         ._position(x, y)

  return tooltip
}
